import { DeliveryTier, DublinLocation } from '../types';
import { getDeliveryTiers } from './firebase';

/**
 * Shipping calculation service
 */
class ShippingService {
  private deliveryTiers: DeliveryTier[] = [];
  private isLoaded = false;

  /**
   * Load delivery tiers from Firebase
   */
  public async loadDeliveryTiers(): Promise<void> {
    try {
      this.deliveryTiers = await getDeliveryTiers();
      this.isLoaded = true;
    } catch (error) {
      console.error('Error loading delivery tiers:', error);
      this.isLoaded = false;
    }
  }

  /**
   * Calculate shipping cost based on postal code and delivery option
   */
  public async calculateShipping(
    postalCode: string,
    deliveryOption: 'collection' | 'delivery'
  ): Promise<number> {
    console.log('🚚 ShippingService.calculateShipping called:', { postalCode, deliveryOption });

    // Collection is always free
    if (deliveryOption === 'collection') {
      console.log('📦 Collection selected - shipping is FREE');
      return 0;
    }

    // Load delivery tiers if not already loaded
    if (!this.isLoaded) {
      console.log('📋 Loading delivery tiers...');
      await this.loadDeliveryTiers();
    }

    console.log('📋 Delivery tiers loaded:', this.deliveryTiers.length, 'tiers');
    console.log('📋 Available tiers:', this.deliveryTiers.map(tier => ({
      name: tier.name,
      price: tier.price,
      isActive: tier.isActive,
      locations: tier.locations.map(loc => loc.routingKey)
    })));

    // If no delivery tiers available, return default shipping cost
    if (this.deliveryTiers.length === 0) {
      console.log('⚠️ No delivery tiers available - using default €5 shipping');
      return 5.00; // Default shipping cost
    }

    // Extract first 3 characters of postal code (routing key)
    const routingKey = postalCode.substring(0, 3).toUpperCase();
    console.log('🔍 Extracted routing key:', routingKey, 'from postal code:', postalCode);

    // Find the delivery tier that contains this routing key
    const matchingTier = this.deliveryTiers.find(tier => {
      const hasMatchingLocation = tier.isActive && 
        tier.locations.some(location => {
          const locationRoutingKey = location.routingKey.toUpperCase();
          const matches = locationRoutingKey === routingKey;
          console.log(`🔍 Checking tier "${tier.name}" location "${location.town}" (${locationRoutingKey}) vs ${routingKey}: ${matches ? '✅ MATCH' : '❌ NO MATCH'}`);
          return matches;
        });
      return hasMatchingLocation;
    });

    console.log('🎯 Matching tier found:', matchingTier ? {
      name: matchingTier.name,
      price: matchingTier.price,
      locations: matchingTier.locations.map(loc => `${loc.town} (${loc.routingKey})`)
    } : 'No matching tier found');

    // Return the price from the matching tier, or default cost if no match
    const finalPrice = matchingTier ? matchingTier.price : 5.00;
    console.log('💰 Final shipping price:', finalPrice);
    return finalPrice;
  }

  /**
   * Get delivery tier information for a postal code
   */
  public async getDeliveryTierInfo(postalCode: string): Promise<{
    tier: DeliveryTier | null;
    routingKey: string;
    isSupported: boolean;
  }> {
    // Load delivery tiers if not already loaded
    if (!this.isLoaded) {
      await this.loadDeliveryTiers();
    }

    const routingKey = postalCode.substring(0, 3).toUpperCase();
    
    const matchingTier = this.deliveryTiers.find(tier => 
      tier.isActive && 
      tier.locations.some(location => 
        location.routingKey.toUpperCase() === routingKey
      )
    );

    return {
      tier: matchingTier || null,
      routingKey,
      isSupported: !!matchingTier
    };
  }

  /**
   * Get all available delivery tiers
   */
  public async getDeliveryTiers(): Promise<DeliveryTier[]> {
    if (!this.isLoaded) {
      await this.loadDeliveryTiers();
    }
    return this.deliveryTiers;
  }

  /**
   * Check if a postal code is supported for delivery
   */
  public async isPostalCodeSupported(postalCode: string): Promise<boolean> {
    const { isSupported } = await this.getDeliveryTierInfo(postalCode);
    return isSupported;
  }
}

// Export singleton instance
export const shippingService = new ShippingService();
export default shippingService;
